// @ts-check
import { Edit, Eye, EyeOff, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import { useHasAnyUnsavedChanges, useRegisterUnsavedChanges } from '../context/UnsavedChangesContext';
import adminProductService from '../services/adminProductService';
import atelierAdminService, { STAGES, STAGE_LABELS } from '../services/atelierAdminService';

const TABS = [
  'Bank details',
  'Content & Timelines',
  'Home & Marketing',
  'Stage reasons',
  'Rejection reasons',
  'Templates',
  'Wholesale prices',
];

// Content & Timelines and Home & Marketing share one /settings/content endpoint (one
// key-value bag). Each tab must only read+write its OWN keys — never the full response —
// or an edit made in one tab (echoed back verbatim from that tab's own fetch) can clobber
// a save just made in the other.
const pick = (obj, keys) => Object.fromEntries(keys.map((k) => [k, (obj && obj[k]) ?? '']));

export default function AtelierSettingsPage() {
  const [tab, setTab] = useState('Bank details');
  // Only one tab's form is ever mounted at a time on this page, so the
  // app-wide "is anything dirty" aggregate is exactly "does the CURRENT
  // tab's form have unsaved changes" here — switching tabs unmounts the
  // active tab's component, which would otherwise silently discard it.
  const hasUnsavedChanges = useHasAnyUnsavedChanges();
  const changeTab = (nextTab) => {
    if (nextTab === tab) return;
    if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Switch tabs anyway?')) return;
    setTab(nextTab);
  };
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-1">Atelier Settings</h1>
      <p className="text-sm text-muted-foreground mb-5">
        Bank details, public content, stage reasons, templates, and wholesale prices.
      </p>
      <div className="flex gap-1 border-b mb-5 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => changeTab(t)}
            className={`flex-shrink-0 whitespace-nowrap px-4 py-2 text-sm -mb-px border-b-2 ${tab === t ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground'}`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 'Bank details' && <BankDetails />}
      {tab === 'Content & Timelines' && <ContentSettings />}
      {tab === 'Home & Marketing' && <HomeMarketingSettings />}
      {tab === 'Stage reasons' && <StageReasons />}
      {tab === 'Rejection reasons' && <AccountRejectionReasons />}
      {tab === 'Templates' && <Templates />}
      {tab === 'Wholesale prices' && <Wholesale />}
    </div>
  );
}

const CONTENT_TAB_KEYS = [
  'atelier_process',
  'atelier_requirements',
  'atelier_terms',
  'atelier_faq',
  'atelier_shipping',
  'atelier_returns',
  'atelier_default_sample_days',
  'atelier_default_production_days',
];

/* ── Public content (markdown) + timeline defaults ── */
function ContentSettings() {
  const toast = useToast();
  const [data, setData] = useState(pick({}, CONTENT_TAB_KEYS));
  const [busy, setBusy] = useState(false);
  // Touched-since-load flag — this tab has no discard/cancel affordance
  // (always visible), so it only needs to register with the app-wide guard.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);

  useEffect(() => {
    atelierAdminService
      .getContent()
      .then((d) => {
        setData(pick(d, CONTENT_TAB_KEYS));
      })
      .catch(() => {});
  }, []);

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setIsDirty(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await atelierAdminService.setContent(pick(data, CONTENT_TAB_KEYS));
      toast.success('Content saved');
      setIsDirty(false);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const block = (key, label, hint) => (
    <div className="mb-5">
      <label className="text-sm font-medium">{label}</label>
      <p className="text-xs text-muted-foreground/70 mb-1">{hint}</p>
      <textarea
        value={data[key]}
        onChange={(e) => set(key, e.target.value)}
        rows={10}
        className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
      />
    </div>
  );

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-muted-foreground mb-4">
        Shown on the atelier site (Markdown supported). Timeline defaults pre-fill new quotes.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5 max-w-sm">
        <label className="text-sm">
          Default sample days
          <input
            type="number"
            value={data.atelier_default_sample_days}
            onChange={(e) => set('atelier_default_sample_days', e.target.value)}
            className="w-full border rounded-lg px-3 py-2 mt-1"
          />
        </label>
        <label className="text-sm">
          Default production days
          <input
            type="number"
            value={data.atelier_default_production_days}
            onChange={(e) => set('atelier_default_production_days', e.target.value)}
            className="w-full border rounded-lg px-3 py-2 mt-1"
          />
        </label>
      </div>
      {block('atelier_process', 'Process & Timelines', 'The public process page — stages and typical durations.')}
      {block('atelier_requirements', 'What We Need From You', 'The design requirements checklist for buyers.')}
      {block('atelier_terms', 'Trade Terms & Policies', 'MOQ, samples, payment, shipping (EXW/FOB), quality, IP, etc.')}
      {block('atelier_faq', 'FAQ', 'Frequently asked questions for B2B buyers.')}
      {block('atelier_shipping', 'Shipping', 'Atelier (Ex-Works / FOB) shipping page.')}
      {block('atelier_returns', 'Returns & Warranty', 'Atelier returns / warranty page for bespoke production.')}
      <button
        disabled={busy}
        onClick={save}
        className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-40"
      >
        Save content
      </button>
    </div>
  );
}

/* ── Home & Marketing: MOQ, announcement bar, homepage/CTA/contact copy ──
   {min}/{max} placeholders in the copy fields are interpolated on the atelier site
   from the Min/Max batch size below, so the batch range only needs to change once. */
const HOME_MARKETING_KEYS = [
  'atelier_min_batch',
  'atelier_max_batch',
  'atelier_announcement_enabled',
  'atelier_announcement_text',
  'atelier_announcement_link',
  'atelier_hero_headline',
  'atelier_hero_subhead',
  'atelier_step1_title',
  'atelier_step1_desc',
  'atelier_step2_title',
  'atelier_step2_desc',
  'atelier_step3_title',
  'atelier_step3_desc',
  'atelier_step4_title',
  'atelier_step4_desc',
  'atelier_portfolio_heading',
  'atelier_portfolio_subtext',
  'atelier_final_cta_heading',
  'atelier_enquire_cta_title',
  'atelier_enquire_cta_subtext',
  'atelier_contact_intro',
  'atelier_contact_email',
];

function HomeMarketingSettings() {
  const toast = useToast();
  const [data, setData] = useState(pick({}, HOME_MARKETING_KEYS));
  const [busy, setBusy] = useState(false);
  // Touched-since-load flag — this tab has no discard/cancel affordance
  // (always visible), so it only needs to register with the app-wide guard.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);

  useEffect(() => {
    atelierAdminService
      .getContent()
      .then((d) => {
        setData(pick(d, HOME_MARKETING_KEYS));
      })
      .catch(() => {});
  }, []);

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setIsDirty(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await atelierAdminService.setContent(pick(data, HOME_MARKETING_KEYS));
      toast.success('Saved');
      setIsDirty(false);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const text = (key, label, hint) => (
    <label className="block text-sm mb-4">
      <span className="font-medium text-foreground/80">{label}</span>
      {hint && <p className="text-xs text-muted-foreground/70 mb-1">{hint}</p>}
      <input
        value={data[key]}
        onChange={(e) => set(key, e.target.value)}
        className="w-full border rounded-lg px-3 py-2 mt-1"
      />
    </label>
  );
  const textarea = (key, label, hint) => (
    <label className="block text-sm mb-4">
      <span className="font-medium text-foreground/80">{label}</span>
      {hint && <p className="text-xs text-muted-foreground/70 mb-1">{hint}</p>}
      <textarea
        value={data[key]}
        onChange={(e) => set(key, e.target.value)}
        rows={3}
        className="w-full border rounded-lg px-3 py-2 mt-1"
      />
    </label>
  );

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-muted-foreground mb-5">
        Shown on the atelier homepage, custom-project CTA, and contact page. Use <code>{'{min}'}</code> and{' '}
        <code>{'{max}'}</code> anywhere the batch range should appear — they&apos;re replaced with the Min/Max batch
        size below, so changing the MOQ updates every mention automatically.
      </p>

      <h3 className="text-sm font-semibold uppercase text-muted-foreground/70 mb-3">Batch size (MOQ)</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 max-w-sm">
        <label className="text-sm">
          Min batch size
          <input
            type="number"
            value={data.atelier_min_batch}
            onChange={(e) => set('atelier_min_batch', e.target.value)}
            className="w-full border rounded-lg px-3 py-2 mt-1"
          />
        </label>
        <label className="text-sm">
          Max batch size
          <input
            type="number"
            value={data.atelier_max_batch}
            onChange={(e) => set('atelier_max_batch', e.target.value)}
            className="w-full border rounded-lg px-3 py-2 mt-1"
          />
        </label>
      </div>
      <p className="text-xs text-muted-foreground/70 -mt-4 mb-6">
        Enforced on project submission, not just display copy.
      </p>

      <h3 className="text-sm font-semibold uppercase text-muted-foreground/70 mb-3">Announcement bar</h3>
      <p className="text-xs text-muted-foreground/70 mb-3">
        Atelier&apos;s own bar — separate from the B2C storefront&apos;s announcements.
      </p>
      <label className="flex items-center gap-2 text-sm mb-3">
        <input
          type="checkbox"
          checked={data.atelier_announcement_enabled === 'true'}
          onChange={(e) => set('atelier_announcement_enabled', e.target.checked ? 'true' : 'false')}
        />
        Show announcement bar
      </label>
      {text('atelier_announcement_text', 'Message')}
      {text('atelier_announcement_link', 'Link', 'e.g. /atelier/process')}

      <h3 className="text-sm font-semibold uppercase text-muted-foreground/70 mb-3 mt-8">Hero</h3>
      {text('atelier_hero_headline', 'Headline')}
      {textarea('atelier_hero_subhead', 'Subhead')}

      <h3 className="text-sm font-semibold uppercase text-muted-foreground/70 mb-3 mt-8">How it works (4 steps)</h3>
      <div className="grid sm:grid-cols-2 gap-x-6">
        {[1, 2, 3, 4].map((n) => (
          <div key={n}>
            {text(`atelier_step${n}_title`, `Step ${n} title`)}
            {textarea(`atelier_step${n}_desc`, `Step ${n} description`)}
          </div>
        ))}
      </div>

      <h3 className="text-sm font-semibold uppercase text-muted-foreground/70 mb-3 mt-8">Portfolio section</h3>
      {text('atelier_portfolio_heading', 'Heading')}
      {textarea('atelier_portfolio_subtext', 'Subtext')}

      <h3 className="text-sm font-semibold uppercase text-muted-foreground/70 mb-3 mt-8">Final CTA</h3>
      {text('atelier_final_cta_heading', 'Heading')}

      <h3 className="text-sm font-semibold uppercase text-muted-foreground/70 mb-3 mt-8">Enquire CTA</h3>
      <p className="text-xs text-muted-foreground/70 mb-3">Shown on product pages inviting a custom commission.</p>
      {text('atelier_enquire_cta_title', 'Button text')}
      {textarea('atelier_enquire_cta_subtext', 'Subtext')}

      <h3 className="text-sm font-semibold uppercase text-muted-foreground/70 mb-3 mt-8">Contact page</h3>
      {textarea('atelier_contact_intro', 'Intro text')}
      {text('atelier_contact_email', 'Contact email')}

      <button
        disabled={busy}
        onClick={save}
        className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-40"
      >
        Save
      </button>
    </div>
  );
}

/* ── Bank details: one field per detail (stored as JSON) ── */
const BANK_FIELDS = [
  ['bank_name', 'Bank name'],
  ['account_name', 'Account holder name'],
  ['account_number', 'Account number'],
  ['ifsc', 'IFSC code'],
  ['swift', 'SWIFT / BIC'],
  ['iban', 'IBAN'],
  ['branch_address', 'Branch / address'],
];

function BankDetails() {
  const toast = useToast();
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  // Touched-since-load flag — this tab has no discard/cancel affordance
  // (always visible), so it only needs to register with the app-wide guard.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);

  useEffect(() => {
    atelierAdminService
      .getBankDetails()
      .then((d) => {
        const raw = d?.value || '';
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            setFields(parsed);
            return;
          }
        } catch {
          /* legacy plain text — start fresh */
        }
        if (raw) setFields({ branch_address: raw });
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await atelierAdminService.setBankDetails(JSON.stringify(fields));
      toast.success('Bank details saved');
      setIsDirty(false);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      <p className="text-sm text-muted-foreground mb-4">
        These appear on every proforma invoice PDF. Leave a field blank to omit it.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {BANK_FIELDS.map(([key, label]) => (
          <label key={key} className="block text-sm">
            <span className="text-muted-foreground">{label}</span>
            <input
              value={fields[key] || ''}
              onChange={(e) => {
                setFields((f) => ({ ...f, [key]: e.target.value }));
                setIsDirty(true);
              }}
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
          </label>
        ))}
      </div>
      <button
        disabled={busy}
        onClick={save}
        className="mt-4 px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-40"
      >
        Save bank details
      </button>
    </div>
  );
}

/* ── Stage reasons: grouped, add, toggle active, delete ── */
function StageReasons() {
  const toast = useToast();
  const [reasons, setReasons] = useState([]);
  const [stage, setStage] = useState('reviewing');
  const [label, setLabel] = useState('');

  const load = useCallback(async () => {
    try {
      setReasons(await atelierAdminService.listStageReasons());
    } catch (e) {
      toast.error(e.message);
    }
  }, [toast]);
  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!label.trim()) return;
    try {
      await atelierAdminService.createStageReason({ to_stage: stage, label: label.trim(), display_order: 0 });
      setLabel('');
      load();
      toast.success('Added');
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };
  const toggle = async (r) => {
    try {
      await atelierAdminService.updateStageReason(r.id, { is_active: !r.is_active });
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };
  const del = async (id) => {
    try {
      await atelierAdminService.deleteStageReason(id);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const grouped = STAGES.map((s) => ({ stage: s, items: reasons.filter((r) => r.to_stage === s) })).filter(
    (g) => g.items.length,
  );

  return (
    <div className="max-w-2xl">
      <div className="flex gap-2 mb-4">
        <select
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          {STAGES.map((s) => (
            <option key={s} value={s}>
              {STAGE_LABELS[s]}
            </option>
          ))}
        </select>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="New preset remark…"
          className="flex-1 border rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={add}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm flex items-center gap-1"
        >
          <Plus size={15} /> Add
        </button>
      </div>
      {grouped.map((g) => (
        <div key={g.stage} className="mb-4">
          <h4 className="text-xs uppercase text-muted-foreground/70 mb-1">
            {STAGE_LABELS[g.stage]} <span className="text-muted-foreground/50">({g.items.length})</span>
          </h4>
          <ul className="space-y-1">
            {g.items.map((r) => (
              <li key={r.id} className="flex justify-between items-center border rounded-lg px-3 py-2 text-sm">
                <span className={r.is_active ? '' : 'text-muted-foreground/70 line-through'}>{r.label}</span>
                <span className="flex items-center gap-3">
                  <button
                    onClick={() => toggle(r)}
                    title={r.is_active ? 'Deactivate' : 'Activate'}
                    className="text-muted-foreground"
                  >
                    {r.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                  <button onClick={() => del(r.id)} className="text-red-600 dark:text-red-400">
                    <Trash2 size={15} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* ── Account rejection reasons: flat preset list, add/toggle active/delete ── */
function AccountRejectionReasons() {
  const toast = useToast();
  const [reasons, setReasons] = useState([]);
  const [label, setLabel] = useState('');

  const load = useCallback(async () => {
    try {
      setReasons(await atelierAdminService.listAccountRejectionReasons());
    } catch (e) {
      toast.error(e.message);
    }
  }, [toast]);
  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!label.trim()) return;
    try {
      await atelierAdminService.createAccountRejectionReason({ label: label.trim(), display_order: reasons.length });
      setLabel('');
      load();
      toast.success('Added');
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };
  const toggle = async (r) => {
    try {
      await atelierAdminService.updateAccountRejectionReason(r.id, { is_active: !r.is_active });
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };
  const del = async (id) => {
    try {
      await atelierAdminService.deleteAccountRejectionReason(id);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-muted-foreground mb-3">
        Preset reasons offered when rejecting a trade account application.
      </p>
      <div className="flex gap-2 mb-4">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="New preset reason…"
          className="flex-1 border rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={add}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm flex items-center gap-1"
        >
          <Plus size={15} /> Add
        </button>
      </div>
      <ul className="space-y-1">
        {reasons.map((r) => (
          <li key={r.id} className="flex justify-between items-center border rounded-lg px-3 py-2 text-sm">
            <span className={r.is_active ? '' : 'text-muted-foreground/70 line-through'}>{r.label}</span>
            <span className="flex items-center gap-3">
              <button
                onClick={() => toggle(r)}
                title={r.is_active ? 'Deactivate' : 'Activate'}
                className="text-muted-foreground"
              >
                {r.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
              <button onClick={() => del(r.id)} className="text-red-600 dark:text-red-400">
                <Trash2 size={15} />
              </button>
            </span>
          </li>
        ))}
        {reasons.length === 0 && <li className="text-muted-foreground/70 text-sm">No preset reasons yet</li>}
      </ul>
    </div>
  );
}

/* ── Wholesale prices (dormant): configured here so the buyer-facing wholesale/
   own-design flow can be switched on later without an admin rebuild ── */
function Wholesale() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const [price, setPrice] = useState('');

  const load = useCallback(async () => {
    try {
      const [wp, prod] = await Promise.all([
        atelierAdminService.listWholesale(),
        adminProductService.getAllProducts({ limit: 500 }).catch(() => ({ items: [] })),
      ]);
      setRows(Array.isArray(wp) ? wp : []);
      setProducts(Array.isArray(prod) ? prod : prod?.items || []);
    } catch (e) {
      toast.error(e.message);
    }
  }, [toast]);
  useEffect(() => {
    load();
  }, [load]);

  const nameById = useMemo(() => {
    const m = {};
    products.forEach((p) => {
      m[p.id] = p.name;
    });
    return m;
  }, [products]);

  const priced = useMemo(() => new Set(rows.map((r) => r.product_id)), [rows]);
  const available = products.filter((p) => !priced.has(p.id));

  const add = async () => {
    if (!productId || !price) {
      toast.error('Pick a product and enter a price');
      return;
    }
    try {
      await atelierAdminService.createWholesale({ product_id: productId, unit_price: Number(price), currency: 'INR' });
      setProductId('');
      setPrice('');
      load();
      toast.success('Added');
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };
  const savePrice = async (row, newPrice) => {
    try {
      await atelierAdminService.updateWholesale(row.id, { unit_price: Number(newPrice) });
      toast.success('Updated');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };
  const del = async (id) => {
    try {
      await atelierAdminService.deleteWholesale(id);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="text-xs bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300 rounded-md px-3 py-2 mb-3">
        The buyer-facing wholesale / own-design flow is currently <strong>off</strong>. Prices set here are ready for
        when it&apos;s switched on.
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        Base wholesale price (INR) per product. Tariff & currency are applied at quote time.
      </p>
      <div className="flex gap-2 mb-5">
        <select
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          className="flex-1 border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Select a product…</option>
          {available.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <input
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          type="number"
          placeholder="Unit price ₹"
          className="w-40 border rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={add}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm flex items-center gap-1"
        >
          <Plus size={15} /> Add
        </button>
      </div>
      <ul className="space-y-2">
        {rows.map((r) => (
          <WholesaleRow
            key={r.id}
            row={r}
            name={nameById[r.product_id] || r.product_id}
            onSave={savePrice}
            onDelete={del}
          />
        ))}
        {rows.length === 0 && <li className="text-muted-foreground/70 text-sm">No wholesale prices set</li>}
      </ul>
    </div>
  );
}

function WholesaleRow({ row, name, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(row.unit_price);
  return (
    <li className="flex justify-between items-center border rounded-lg px-3 py-2 text-sm">
      <span className="font-medium">{name}</span>
      <span className="flex items-center gap-3">
        {editing ? (
          <>
            <input
              value={val}
              onChange={(e) => setVal(e.target.value)}
              type="number"
              className="w-28 border rounded-lg px-2 py-1"
            />
            <button
              onClick={() => {
                onSave(row, val);
                setEditing(false);
              }}
              className="text-green-700 dark:text-green-300"
            >
              Save
            </button>
            <button
              onClick={() => {
                setVal(row.unit_price);
                setEditing(false);
              }}
              className="text-muted-foreground/70"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span>₹{row.unit_price}</span>
            <button onClick={() => setEditing(true)} className="text-blue-600 dark:text-blue-400">
              <Edit size={15} />
            </button>
          </>
        )}
        <button onClick={() => onDelete(row.id)} className="text-red-600 dark:text-red-400">
          <Trash2 size={15} />
        </button>
      </span>
    </li>
  );
}

/* ── Templates: email-templates-style list + editor modal ── */
function Templates() {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  // Touched-since-open flag — `editing` doubles as both the loaded record
  // and the in-progress edits, so dirty is tracked separately rather than
  // wrapping setEditing itself (opening for edit also calls setEditing).
  const [isDirty, setIsDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      setTemplates(await atelierAdminService.listTemplates());
    } catch (e) {
      toast.error(e.message);
    }
  }, [toast]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      await atelierAdminService.updateTemplate(editing.id, { subject: editing.subject, body: editing.body });
      toast.success('Template saved');
      setIsDirty(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };

  // X button and footer Cancel both dismiss the modal — confirm first if
  // there's anything to lose. save()'s success path bypasses this.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setIsDirty(false);
    setEditing(null);
  };

  const TYPE_LABELS = { outreach: 'Outreach email', proposal: 'Proposal', nda: 'NDA' };

  return (
    <div className="max-w-3xl">
      <div className="grid sm:grid-cols-2 gap-3">
        {templates.map((t) => (
          <div key={t.id} className="border rounded-lg p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-muted-foreground/70">
                  {TYPE_LABELS[t.template_type] || t.template_type}
                </div>
              </div>
              <button
                onClick={() => {
                  setEditing({ ...t });
                  setIsDirty(false);
                }}
                className="text-blue-600 dark:text-blue-400 flex items-center gap-1 text-sm"
              >
                <Edit size={14} /> Edit
              </button>
            </div>
            {t.subject && <div className="text-xs text-muted-foreground truncate">Subject: {t.subject}</div>}
            <div className="text-xs text-muted-foreground/70 mt-2 line-clamp-2">
              {(t.body || '').replace(/<[^>]+>/g, ' ').slice(0, 120)}…
            </div>
          </div>
        ))}
        {templates.length === 0 && <p className="text-muted-foreground/70 text-sm">No templates</p>}
      </div>

      {editing && (
        <Modal
          maxWidth="2xl"
          closeOnBackdrop={false}
          onClose={handleCloseClick}
          className="rounded-lg"
          hasUnsavedChanges={isDirty}
        >
          <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-card">
            <h3 className="font-semibold">
              {editing.name}{' '}
              <span className="text-xs text-muted-foreground/70 font-normal">
                ({TYPE_LABELS[editing.template_type] || editing.template_type})
              </span>
            </h3>
            <button onClick={handleCloseClick}>
              <X size={20} />
            </button>
          </div>
          <div className="p-5 space-y-4">
            {editing.template_type !== 'nda' && (
              <label className="block text-sm">
                <span className="text-muted-foreground">Subject</span>
                <input
                  value={editing.subject || ''}
                  onChange={(e) => {
                    setEditing({ ...editing, subject: e.target.value });
                    setIsDirty(true);
                  }}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </label>
            )}
            <label className="block text-sm">
              <span className="text-muted-foreground">Body</span>
              <textarea
                value={editing.body}
                onChange={(e) => {
                  setEditing({ ...editing, body: e.target.value });
                  setIsDirty(true);
                }}
                rows={14}
                className="w-full border rounded-md px-3 py-2 mt-1 font-mono text-xs"
              />
            </label>
            {Array.isArray(editing.variables) && editing.variables.length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">Available placeholders (click to copy):</div>
                <div className="flex flex-wrap gap-1">
                  {editing.variables.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(`{{${v}}}`)}
                      className="text-xs bg-muted hover:bg-accent rounded-md px-2 py-1 font-mono"
                    >{`{{${v}}}`}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 p-5 border-t sticky bottom-0 bg-card">
            <button onClick={save} className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm">
              Save
            </button>
            <button onClick={handleCloseClick} className="px-5 py-2 rounded-lg bg-muted text-sm">
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
